import { useState, FormEvent } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { supabase } from "../../../lib/supabase";

function validatePassword(pwd: string): string | null {
  if (pwd.length < 8) return "At least 8 characters required";
  if (!/[0-9]/.test(pwd)) return "Must contain at least 1 number";
  if (!/[^a-zA-Z0-9]/.test(pwd)) return "Must contain at least 1 special character";
  return null;
}

function getStrength(pwd: string) {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  if (pwd.length >= 12) score++;
  return {
    score,
    label: ["", "Weak", "Fair", "Good", "Strong"][score],
    color: ["", "bg-red-500", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"][score],
  };
}

export default function SetNewPasswordScreen() {
  const { isDark } = useTheme();
  const { clearRecovery, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = getStrength(password);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const pwErr = validatePassword(password);
    if (pwErr) { setError(pwErr); return; }
    if (password !== confirm) { setError("Passwords don\u2019t match."); return; }
    setError(null);
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setDone(true);
    setLoading(false);
  };

  const handleContinue = async () => {
    clearRecovery();
  };

  const inputWrap = `flex items-center gap-2 w-full px-4 py-3 rounded-xl border transition-all duration-150 focus-within:ring-2 ${
    isDark
      ? "bg-white/5 border-white/10 focus-within:ring-white/15 focus-within:border-white/25"
      : "bg-white border-black/10 focus-within:ring-black/8 focus-within:border-black/25"
  }`;
  const inputInner = `flex-1 bg-transparent outline-none font-inter text-sm ${isDark ? "text-white placeholder:text-[#444]" : "text-[#111] placeholder:text-[#aaa]"}`;
  const eyeBtn = `w-4 h-4 flex items-center justify-center cursor-pointer flex-shrink-0 ${isDark ? "text-[#444] hover:text-[#888]" : "text-[#ccc] hover:text-[#888]"}`;

  if (done) {
    return (
      <div className="text-center py-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
          <i className={`ri-checkbox-circle-line text-2xl ${isDark ? "text-emerald-400" : "text-emerald-500"}`} />
        </div>
        <h3 className={`font-syne font-semibold text-lg mb-2 ${isDark ? "text-white" : "text-[#111]"}`}>
          Password updated!
        </h3>
        <p className={`font-inter text-sm mb-6 ${isDark ? "text-[#888]" : "text-[#666]"}`}>
          Your new password has been set. You&apos;re good to go.
        </p>
        <button
          type="button"
          onClick={handleContinue}
          className={`w-full font-inter font-medium text-sm py-3 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 ${
            isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
          }`}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
          <i className={`ri-lock-password-line text-xl ${isDark ? "text-white/60" : "text-[#666]"}`} />
        </div>
        <h3 className={`font-syne font-semibold text-lg mb-1.5 ${isDark ? "text-white" : "text-[#111]"}`}>
          Set a new password
        </h3>
        <p className={`font-inter text-sm ${isDark ? "text-[#888]" : "text-[#666]"}`}>
          Choose a strong password for your account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {/* New password */}
        <div>
          <div className={inputWrap}>
            <input
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              required
              className={inputInner}
              autoFocus
            />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className={eyeBtn}>
              <i className={`${showPwd ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
            </button>
          </div>
          {password.length > 0 && (
            <div className="mt-1.5 space-y-1.5 px-0.5">
              <div className="flex items-center gap-2">
                <div className={`flex-1 h-1 rounded-full overflow-hidden ${isDark ? "bg-white/8" : "bg-black/8"}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                    style={{ width: `${(strength.score / 4) * 100}%` }}
                  />
                </div>
                {strength.label && (
                  <span className={`font-inter text-[10px] ${strength.score <= 1 ? "text-red-400" : strength.score === 2 ? "text-amber-400" : "text-emerald-400"}`}>
                    {strength.label}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                {[
                  { ok: password.length >= 8, label: "8+ characters" },
                  { ok: /[0-9]/.test(password), label: "1 number" },
                  { ok: /[^a-zA-Z0-9]/.test(password), label: "1 special character" },
                ].map((req) => (
                  <span key={req.label} className={`font-inter text-[10px] flex items-center gap-1 ${req.ok ? "text-emerald-400" : isDark ? "text-[#555]" : "text-[#bbb]"}`}>
                    <i className={`${req.ok ? "ri-checkbox-circle-line" : "ri-circle-line"} text-[10px]`} />
                    {req.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <div className={inputWrap}>
            <input
              type={showConfirm ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm password"
              required
              className={inputInner}
            />
            <button type="button" onClick={() => setShowConfirm((v) => !v)} className={eyeBtn}>
              <i className={`${showConfirm ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
            </button>
          </div>
          {confirm.length > 0 && (
            <p className={`font-inter text-[10px] mt-1 ${password === confirm ? "text-emerald-400" : "text-red-400"}`}>
              <i className={`${password === confirm ? "ri-checkbox-circle-line" : "ri-error-warning-line"} mr-1`} />
              {password === confirm ? "Passwords match" : "Passwords don\u2019t match"}
            </p>
          )}
        </div>

        {error && <p className="font-inter text-xs text-red-400 px-0.5">{error}</p>}

        <button
          type="submit"
          disabled={loading || !password || !confirm}
          className={`w-full font-inter font-medium text-sm py-3 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-1 ${
            isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
          }`}
        >
          {loading ? "Updating…" : "Set New Password"}
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => signOut()}
          className={`font-inter text-xs cursor-pointer transition-colors ${isDark ? "text-[#444] hover:text-[#666]" : "text-[#ccc] hover:text-[#999]"}`}
        >
          Cancel &amp; sign out
        </button>
      </div>
    </div>
  );
}
